'use client'

import { useApiReactQuery } from '@/api/useApiWithReactQuery'
import { usePhoneValidation } from '@/hooks/usePhoneValidation'
import { yupResolver } from '@hookform/resolvers/yup'
import { Radio, Stack, TextInput } from '@mantine/core'
import {
  HONEYPOT_FIELD,
  LeadFormHoneypot,
  ReCaptchaProvider,
  TOO_MANY_SUBMISSIONS_MESSAGE,
  trackMetaEvent,
  useRecaptchaToken,
} from '@repo/ui'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import * as yup from 'yup'

// Keys must match LandlordLead.PropertyLocation / HeardAboutUs in the Django API
const PROPERTY_LOCATIONS = [
  { value: 'red_sea', label: 'Red Sea' },
  { value: 'north_coast', label: 'North Coast' },
  { value: 'cairo', label: 'Cairo' },
  { value: 'other', label: 'Other' },
]

const HEARD_ABOUT_US = [
  { value: 'social_media', label: 'Social Media' },
  { value: 'email_campaign', label: 'Email Campaign' },
  { value: 'whatsapp_message', label: 'WhatsApp Message' },
  { value: 'facebook_post', label: 'Facebook Post' },
  { value: 'partment_website', label: 'Partment Website' },
  { value: 'on_ground_booth', label: 'On-Ground Booth' },
  { value: 'networking_event', label: 'Networking Event' },
  { value: 'search_engine', label: 'Search Engine' },
  { value: 'webinar', label: 'Webinar' },
  { value: 'referral', label: 'Referral' },
  { value: 'other', label: 'Other' },
]

const schema = yup.object().shape({
  first_name: yup.string().trim().required('First name is required').max(80),
  last_name: yup.string().trim().required('Last name is required').max(80),
  email: yup.string().trim().email('Enter a valid email address').required('Email is required'),
  phone: yup.string().required('Phone number is required'),
  property_location: yup.string().required('Please choose a location'),
  compound_project: yup.string().trim().max(200).optional(),
  heard_about_us: yup.string().required('Please tell us how you heard about us'),
})

const inputClassNames = {
  label: 'text-[#07122E] text-sm font-medium mb-2',
  input: 'rounded-lg border-gray-300 focus:border-[#212B49] h-11',
}

// The API requires a reCAPTCHA token on this form (it auto-replies to the typed address)
const LandlordLeadForm = () => (
  <ReCaptchaProvider>
    <LandlordLeadFormBody />
  </ReCaptchaProvider>
)

const errorMessage = (err) => {
  if (err?.response?.status === 429) return TOO_MANY_SUBMISSIONS_MESSAGE
  return err?.response?.data?.message && err.response.data.message !== 'Something went wrong'
    ? err.response.data.message
    : 'Something went wrong. Please try again.'
}

const LandlordLeadFormBody = () => {
  const { post } = useApiReactQuery()
  const getRecaptchaToken = useRecaptchaToken()
  const { validatePhoneNumber } = usePhoneValidation({ required: true })
  const [submitted, setSubmitted] = useState(false)
  const [phoneCountry, setPhoneCountry] = useState('eg')

  const {
    control,
    handleSubmit,
    register,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      property_location: '',
      compound_project: '',
      heard_about_us: '',
      [HONEYPOT_FIELD]: '',
    },
  })

  const onSubmit = async (values) => {
    const phoneError = validatePhoneNumber(values.phone, phoneCountry)
    if (phoneError) {
      setError('phone', { message: phoneError })
      return
    }

    // Read at submit time from the URL (no useSearchParams → no Suspense boundary needed)
    const query = new URLSearchParams(window.location.search)
    const recaptchaToken = await getRecaptchaToken('landlord_lead')
    const payload = {
      ...values,
      phone: `+${values.phone.replace(/\D/g, '')}`,
      source_url: window.location.href,
      utm_source: query.get('utm_source') || '',
      utm_medium: query.get('utm_medium') || '',
      utm_campaign: query.get('utm_campaign') || '',
      ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
    }

    return new Promise((resolve) => {
      post.mutate(
        { url: 'landlord-lead/', data: payload, authRequired: false, version: 'v2' },
        {
          onSuccess: (res) => {
            if (res?.code === 200) {
              trackMetaEvent('Lead_Landlord')
              setSubmitted(true)
            } else {
              setError('root', { message: res?.message?.message || 'Something went wrong. Please try again.' })
            }
            resolve()
          },
          onError: (err) => {
            setError('root', { message: errorMessage(err) })
            resolve()
          },
        },
      )
    })
  }

  if (submitted) {
    return (
      <div className='w-full mx-auto bg-white rounded-xl shadow-lg px-8 py-12 text-center' style={{ maxWidth: '800px' }}>
        <h3 className='text-2xl font-semibold text-[#212B49] mb-3'>Thank you!</h3>
        <p className='text-[#23496D]'>
          We&apos;ve received your details. A member of our team will contact you within 1–2 business days.
        </p>
      </div>
    )
  }

  return (
    <div className='w-full mx-auto bg-white rounded-xl shadow-lg' style={{ maxWidth: '800px' }}>
      <div className='border-b border-gray-200 px-6 md:px-10 py-7 text-center'>
        <h3 className='text-2xl md:text-[28px] font-semibold text-[#212B49]'>Partment Stays Registration Form</h3>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className='px-6 md:px-10 py-8'>
        <LeadFormHoneypot {...register(HONEYPOT_FIELD)} />
        <p className='text-[#23496D] mb-8'>
          Turn your home into a high-performing short-term rental with Partment Stays. We&apos;ve managed 100+
          properties across the Red Sea, North Coast, and Cairo.
        </p>

        <Stack gap='lg'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <TextInput
              label='First Name'
              withAsterisk
              classNames={inputClassNames}
              error={errors.first_name?.message}
              {...register('first_name')}
            />
            <TextInput
              label='Last Name'
              withAsterisk
              classNames={inputClassNames}
              error={errors.last_name?.message}
              {...register('last_name')}
            />
          </div>

          <div>
            <label className='text-[#07122E] text-sm font-medium mb-2 block'>
              Phone <span className='text-red-500'>*</span>
            </label>
            <Controller
              name='phone'
              control={control}
              render={({ field }) => (
                <PhoneInput
                  country={phoneCountry}
                  value={field.value}
                  onChange={(val, data) => {
                    field.onChange(val || '')
                    setPhoneCountry(data?.countryCode?.toLowerCase() || 'eg')
                  }}
                  onBlur={field.onBlur}
                  containerClass='w-full !flex !items-center'
                  inputClass='!w-full !h-11 !rounded-lg !border-gray-300 !text-base'
                  buttonClass='!rounded-l-lg !border-gray-300'
                  inputProps={{ name: 'phone', required: true }}
                />
              )}
            />
            {errors.phone && <p className='text-red-500 text-xs mt-1'>{errors.phone.message}</p>}
          </div>

          <TextInput
            label='Email'
            type='email'
            withAsterisk
            classNames={inputClassNames}
            error={errors.email?.message}
            {...register('email')}
          />

          <Controller
            name='property_location'
            control={control}
            render={({ field }) => (
              <Radio.Group
                label='Property Location'
                withAsterisk
                value={field.value}
                onChange={field.onChange}
                error={errors.property_location?.message}
                classNames={{ label: inputClassNames.label }}
              >
                <Stack gap='sm' mt='xs'>
                  {PROPERTY_LOCATIONS.map((opt) => (
                    <Radio key={opt.value} value={opt.value} label={opt.label} color='#212B49' />
                  ))}
                </Stack>
              </Radio.Group>
            )}
          />

          <TextInput
            label='Compound/Project'
            classNames={inputClassNames}
            error={errors.compound_project?.message}
            {...register('compound_project')}
          />

          <Controller
            name='heard_about_us'
            control={control}
            render={({ field }) => (
              <Radio.Group
                label='How did you hear about us?'
                withAsterisk
                value={field.value}
                onChange={field.onChange}
                error={errors.heard_about_us?.message}
                classNames={{ label: inputClassNames.label }}
              >
                <Stack gap='sm' mt='xs'>
                  {HEARD_ABOUT_US.map((opt) => (
                    <Radio key={opt.value} value={opt.value} label={opt.label} color='#212B49' />
                  ))}
                </Stack>
              </Radio.Group>
            )}
          />

          {errors.root && <p className='text-red-500 text-sm text-center'>{errors.root.message}</p>}

          <div className='text-center pt-2'>
            <button
              type='submit'
              disabled={isSubmitting}
              className='rounded-full bg-[#19AD30] hover:bg-[#158f28] disabled:opacity-60 text-white font-semibold px-12 py-3 transition-colors'
            >
              {isSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </Stack>
      </form>
    </div>
  )
}

export default LandlordLeadForm
